import React, { useState } from "react";
import { AppBar, Toolbar, Box, Divider, IconButton, Menu, MenuItem } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger } from "../ui/tabs";
import { COLORS } from "../../constants/colors";
import { useAccount } from '../../contexts/AccountContext';
import { Menu as MenuIcon } from "lucide-react";

const Navbar = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { userData, clearUserData } = useAccount();
    const adminEmails = process.env.REACT_APP_ADMIN_EMAILS.split(',');
    const isAdmin = userData?.email && adminEmails.includes(userData.email); 
    
    // Mobile menu state
    const [mobileMenuAnchor, setMobileMenuAnchor] = useState(null);
    const isMobile = window.innerWidth <= 768;

    const handleMenuOpen = (event) => {
        setMobileMenuAnchor(event.currentTarget);
    };

    const handleMenuClose = () => {
        setMobileMenuAnchor(null);
    };

    const handleNavigation = (path) => {
        navigate(path);
        handleMenuClose();
    };

    const handleLogout = () => {
        clearUserData();
        navigate('/', { replace: true });
    };

    const navItems = [
        { label: <span className="font-bold">Blast Library</span>, path: '/blasts' },
        { label: <span className="font-bold">Sent Blasts</span>, path: '/sent-blasts' },
        { label: <span className="font-bold">Dashboard</span>, path: '/users' },
        { label: <span className="font-bold">Patron Info</span>, path: '/patron-info' },
        ...(isAdmin ? [{ label: <span className="font-bold">Admin Portal</span>, path: '/admin' }] : [])
    ];

    return (
        <Box className="bg-white/95 backdrop-blur-lg rounded-2xl shadow-xl overflow-hidden">
            <AppBar
                position="static"
                sx={{
                    marginBottom: 0,
                    boxShadow: "none",
                    bgcolor: "white",
                    color: COLORS.PURPLE.hex,
                    borderTopLeftRadius: "8px",
                    borderTopRightRadius: "8px",
                }}
            >
                <Toolbar sx={{ justifyContent: "space-between" }}>
                    {isMobile ? (
                        // Mobile View
                        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <IconButton
                                onClick={handleMenuOpen}
                                sx={{ color: COLORS.PURPLE.hex }}
                            >
                                <MenuIcon />
                            </IconButton>
                            <Menu
                                anchorEl={mobileMenuAnchor}
                                open={Boolean(mobileMenuAnchor)}
                                onClose={handleMenuClose}
                                PaperProps={{
                                    sx: {
                                        mt: 1.5,
                                        width: 200,
                                        '& .MuiMenuItem-root': {
                                            typography: 'body1',
                                            fontSize: '1rem',
                                            py: 1.5
                                        }
                                    }
                                }}
                            >
                                {navItems.map((item) => (
                                    <MenuItem
                                        key={item.path}
                                        onClick={() => handleNavigation(item.path)}
                                        selected={location.pathname === item.path}
                                    >
                                        {item.label}
                                    </MenuItem>
                                ))}
                                <Divider sx={{ my: 1 }} />
                                <MenuItem 
                                    onClick={handleLogout}
                                    sx={{ color: 'red' }}
                                >
                                    Logout
                                </MenuItem>
                            </Menu>
                            <Box sx={{ color: COLORS.PURPLE.hex, fontWeight: 'bold' }}>
                                {navItems.find(item => item.path === location.pathname)?.label || 'Menu'}
                            </Box>
                            <Box width={28} /> {/* Spacer to balance the menu icon */}
                        </Box>
                    ) : (
                        // Desktop View
                        <Tabs defaultValue={location.pathname} className="w-full">
                            <TabsList className="w-full flex justify-between">
                                <div className="flex gap-4">
                                    {navItems.map((item) => (
                                        <TabsTrigger
                                            key={item.path}
                                            to={item.path}
                                            isActive={location.pathname === item.path}
                                        >
                                            {item.label}
                                        </TabsTrigger>
                                    ))}
                                </div>
                                <TabsTrigger
                                    onClick={handleLogout}
                                    to="#"
                                    className="text-red-500 hover:text-red-700"
                                >
                                    Logout
                                </TabsTrigger>
                            </TabsList>
                        </Tabs>
                    )}
                </Toolbar>
            </AppBar>
            <Divider sx={{ borderColor: "rgba(0, 0, 0, 0.12)", width: "100%" }} />
        </Box>
    );
};

export default Navbar;
